"use client"

import { useEffect, useState } from "react"
import { useParams } from "react-router-dom"
import DashboardTemplate from "../templates/DashboardTemplate"
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet"
import { io } from "socket.io-client"
import "leaflet/dist/leaflet.css"
import L from "leaflet"
import { Map, Truck, Navigation, Clock, CheckCircle, XCircle } from "lucide-react"

// Leaflet icon fix
delete L.Icon.Default.prototype._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: require("leaflet/dist/images/marker-icon-2x.png"),
  iconUrl: require("leaflet/dist/images/marker-icon.png"),
  shadowUrl: require("leaflet/dist/images/marker-shadow.png"),
})

const Spinner = () => (
  <div className="flex justify-center items-center py-8">
    <svg
      className="animate-spin h-10 w-10 text-amber-500"
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
    >
      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v8z" />
    </svg>
  </div>
)

const StatusBadge = ({ status }) => {
  const getStatusConfig = () => {
    switch (status) {
      case "Pending":
        return { icon: Clock, color: "bg-amber-100 text-amber-800 border-amber-200" }
      case "Accepted":
        return { icon: CheckCircle, color: "bg-blue-100 text-blue-800 border-blue-200" }
      case "In Progress":
        return { icon: Truck, color: "bg-purple-100 text-purple-800 border-purple-200" }
      case "Ready":
        return { icon: Navigation, color: "bg-green-100 text-green-800 border-green-200" }
      case "Completed":
        return { icon: CheckCircle, color: "bg-green-100 text-green-800 border-green-200" }
      case "Cancelled":
        return { icon: XCircle, color: "bg-red-100 text-red-800 border-red-200" }
      default:
        return { icon: Clock, color: "bg-gray-100 text-gray-800 border-gray-200" }
    }
  }

  const { icon: Icon, color } = getStatusConfig()

  return (
    <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium border ${color}`}>
      <Icon className="w-4 h-4 mr-1.5" />
      {status}
    </span>
  )
}

export default function TrackDeliveryPage() {
  const { orderId } = useParams()
  const [driverPos, setDriverPos] = useState(null)
  const [status, setStatus] = useState("")
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState(null)
  const [orderDetails, setOrderDetails] = useState(null)
  const [estimatedTime, setEstimatedTime] = useState(null)

  useEffect(() => {
    let socket

    const connectToSocket = async () => {
      try {
        // 1) connect
        socket = io(process.env.REACT_APP_SOCKET_URL, {
          transports: ["websocket"],
          withCredentials: true,
        })

        // 2) join this order's room
        socket.emit("joinOrderRoom", orderId)

        // 3) listen
        socket.on("locationUpdate", (coords) => {
          setDriverPos(coords)
          setLoading(false)
        })

        socket.on("statusUpdate", (s) => {
          setStatus(s)
        })

        socket.on("estimatedTimeUpdate", (time) => {
          setEstimatedTime(time)
        })

        socket.on("orderDetails", (details) => {
          setOrderDetails(details)
        })

        // Set a timeout to show a message if we don't get driver location
        setTimeout(() => {
          setLoading(false)
        }, 5000)

        // 4) request initial data
        socket.emit("requestOrderDetails", orderId)
      } catch (err) {
        console.error("Socket connection error:", err)
        setError("Failed to connect to tracking service")
        setLoading(false)
      }
    }

    connectToSocket()

    // 5) cleanup
    return () => {
      if (socket) {
        socket.off("locationUpdate")
        socket.off("statusUpdate")
        socket.off("estimatedTimeUpdate")
        socket.off("orderDetails")
        socket.disconnect()
      }
    }
  }, [orderId])

  return (
    <DashboardTemplate>
      <div className="p-6 space-y-6">
        <div className="flex items-center mb-2">
          <Map className="h-7 w-7 mr-2 text-amber-600" />
          <h1 className="text-3xl font-bold text-gray-800">Track Delivery</h1>
        </div>

        {loading ? (
          <div className="flex flex-col items-center justify-center py-12">
            <Spinner />
            <p className="mt-4 text-gray-600">Connecting to tracking service...</p>
          </div>
        ) : error ? (
          <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-lg">
            <p>{error}</p>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <div className="flex flex-wrap items-center justify-between gap-4 mb-4">
                <div>
                  <h2 className="text-lg font-semibold text-gray-800">Order #{orderId.slice(-6).toUpperCase()}</h2>
                  {orderDetails && (
                    <p className="text-gray-600 text-sm mt-1">{orderDetails.restaurant?.name || "Restaurant"}</p>
                  )}
                </div>
                {status && <StatusBadge status={status} />}
              </div>

              {estimatedTime && (
                <div className="mb-4 bg-amber-50 border border-amber-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <Clock className="h-5 w-5 text-amber-600 mr-2" />
                    <div>
                      <p className="font-medium text-gray-800">Estimated Arrival</p>
                      <p className="text-amber-800">{estimatedTime} minutes</p>
                    </div>
                  </div>
                </div>
              )}

              {orderDetails && orderDetails.driver && (
                <div className="mb-4 bg-blue-50 border border-blue-200 rounded-lg p-3">
                  <div className="flex items-center">
                    <Truck className="h-5 w-5 text-blue-600 mr-2" />
                    <div>
                      <p className="font-medium text-gray-800">Driver</p>
                      <p className="text-gray-700">{orderDetails.driver.name}</p>
                      {orderDetails.driver.phone && (
                        <a href={`tel:${orderDetails.driver.phone}`} className="text-blue-600 text-sm hover:underline">
                          {orderDetails.driver.phone}
                        </a>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="h-96 rounded-lg overflow-hidden bg-gray-100 border border-gray-200 shadow-sm">
              {driverPos ? (
                <MapContainer
                  center={[driverPos.lat, driverPos.lng]}
                  zoom={15}
                  style={{ height: "100%", width: "100%" }}
                >
                  <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  />
                  <Marker position={[driverPos.lat, driverPos.lng]}>
                    <Popup>
                      <div className="text-center">
                        <p className="font-bold">Your driver is here</p>
                        {estimatedTime && <p className="text-sm">Arriving in approximately {estimatedTime} minutes</p>}
                      </div>
                    </Popup>
                  </Marker>

                  {orderDetails && orderDetails.restaurant && orderDetails.restaurant.location && (
                    <Marker position={[orderDetails.restaurant.location.lat, orderDetails.restaurant.location.lng]}>
                      <Popup>
                        <div className="text-center">
                          <p className="font-bold">{orderDetails.restaurant.name}</p>
                          <p className="text-sm">Restaurant location</p>
                        </div>
                      </Popup>
                    </Marker>
                  )}

                  {orderDetails && orderDetails.deliveryAddress && orderDetails.deliveryAddress.location && (
                    <Marker
                      position={[orderDetails.deliveryAddress.location.lat, orderDetails.deliveryAddress.location.lng]}
                    >
                      <Popup>
                        <div className="text-center">
                          <p className="font-bold">Delivery Location</p>
                          <p className="text-sm">{orderDetails.deliveryAddress.text}</p>
                        </div>
                      </Popup>
                    </Marker>
                  )}
                </MapContainer>
              ) : (
                <div className="h-full flex flex-col items-center justify-center bg-amber-50">
                  <Navigation className="h-12 w-12 text-amber-500 mb-3" />
                  <h3 className="text-lg font-medium text-gray-900">Waiting for driver location</h3>
                  <p className="mt-2 text-sm text-gray-500 max-w-md text-center px-4">
                    The driver's location will appear on the map once they start the delivery. This may take a few
                    moments.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="font-medium text-gray-800 mb-2">Delivery Status Updates</h3>
              <div className="space-y-3">
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-500 flex items-center justify-center mt-0.5">
                    <CheckCircle className="h-3 w-3 text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-700">Order placed</p>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
      </div>
    </DashboardTemplate>
  )
}
